import { Building2, Crown } from 'lucide-react'
import { orgAccess } from '../../admin/ui'
import { getInitials, ROLE_LABELS, ROLE_COLORS } from './userShared'

// Role hierarchy as an organogram (HQ only). The platform hierarchy is:
//   Super Admin (HQ)  →  Admin (organisation)  →  Manager (branch)  →  Staff
// Super Admins sit above every organisation; each organisation is drawn as its
// own tier tree of Admins → Managers → Staff.

const TIERS = [
  { role: 'admin', label: 'Admins', dot: 'bg-blue-500', line: 'border-blue-200' },
  { role: 'manager', label: 'Managers', dot: 'bg-green-500', line: 'border-green-200' },
  { role: 'staff', label: 'Staff', dot: 'bg-gray-400', line: 'border-gray-200' },
]

function Person({ user, branches }) {
  const branch = branches.find((b) => b.id === user.branchId)
  const dim = user.isActive === false
  return (
    <div className={`flex items-center gap-2.5 rounded-xl border border-gray-100 bg-white px-3 py-2 ${dim ? 'opacity-50' : ''}`}>
      <span className={`w-7 h-7 rounded-full grid place-items-center text-[10.5px] font-bold flex-none ${
        dim ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-700'
      }`}>
        {getInitials(user.name)}
      </span>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-[13px] font-semibold text-gray-800 truncate">{user.name}</p>
          {dim && <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 rounded-full px-1.5">Inactive</span>}
        </div>
        <p className="text-[11px] text-gray-400 truncate">
          {user.email || `@${user.username}`}
          {branch ? <span className="text-gray-400"> · {branch.name}</span> : null}
        </p>
      </div>
    </div>
  )
}

function OrgTree({ org, members, branches }) {
  const access = orgAccess(org)
  return (
    <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4">
      {/* Org node */}
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg bg-teal-600 text-white grid place-items-center flex-none">
          <Building2 className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-800 truncate">{org.name}</p>
          <div className="flex items-center gap-1.5">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${access.cls}`}>{access.label}</span>
            <span className="text-[11px] text-gray-400">{members.length} {members.length === 1 ? 'person' : 'people'}</span>
          </div>
        </div>
      </div>

      {/* Tiers */}
      <div className="space-y-3">
        {TIERS.map(({ role, label, dot, line }) => {
          const tier = members.filter((m) => m.role === role)
          if (tier.length === 0) return null
          return (
            <div key={role} className={`pl-3 border-l-2 ${line}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                <span className="text-[10.5px] font-bold uppercase tracking-wider text-gray-400">{label}</span>
                <span className="text-[10.5px] text-gray-300">({tier.length})</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {tier.map((u) => <Person key={u.id} user={u} branches={branches} />)}
              </div>
            </div>
          )
        })}
        {members.length === 0 && (
          <p className="text-xs text-gray-400 pl-3">No members.</p>
        )}
      </div>
    </div>
  )
}

export default function RoleOrganogram({ users, organizations, branches }) {
  const superAdmins = users.filter((u) => u.role === 'superadmin')

  // One tree per organisation, plus a bucket for anyone without an org.
  const byOrg = new Map()
  for (const u of users) {
    if (u.role === 'superadmin') continue
    const key = u.organizationId || 'none'
    if (!byOrg.has(key)) byOrg.set(key, [])
    byOrg.get(key).push(u)
  }
  const orgTrees = organizations
    .map((org) => ({ org, members: byOrg.get(org.id) || [] }))
    .sort((a, b) => a.org.name.localeCompare(b.org.name))
  const unassigned = byOrg.get('none') || []

  return (
    <div className="space-y-5">
      {/* HQ node — super admins sit above every organisation */}
      <div className="rounded-2xl border border-teal-200 bg-teal-50/60 p-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-lg bg-teal-600 text-white grid place-items-center flex-none">
            <Crown className="w-4 h-4" />
          </span>
          <div>
            <p className="text-sm font-bold text-gray-800">PetroTrack HQ</p>
            <p className="text-[11px] text-gray-500">Super Admins — platform-wide access</p>
          </div>
        </div>
        {superAdmins.length === 0 ? (
          <p className="text-xs text-gray-400 pl-1">No super admins.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {superAdmins.map((u) => <Person key={u.id} user={u} branches={branches} />)}
          </div>
        )}
      </div>

      {/* Connector cue */}
      <div className="flex items-center gap-2 pl-1">
        <span className="h-4 w-px bg-gray-200 ml-3.5" />
        <span className="text-[10.5px] font-bold uppercase tracking-wider text-gray-400">Organisations</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {orgTrees.map(({ org, members }) => (
          <OrgTree key={org.id} org={org} members={members} branches={branches} />
        ))}
      </div>

      {unassigned.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
          <p className="text-sm font-bold text-gray-800 mb-3">Unassigned</p>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {unassigned.map((u) => (
              <div key={u.id} className="flex items-center gap-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role]}</span>
                <Person user={u} branches={branches} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
